import React, { useState, useEffect, useRef, useCallback } from 'react';
import { User } from '../../types/user';
import { useTheme } from '../../contexts/ThemeContext';
import { Search, X, Check, ChevronDown, Loader } from 'lucide-react';

interface UserSelectorProps {
    fetchUsers: (query: string, page: number) => Promise<User[]>;
    selectedIds: number | number[];
    selectedUsers?: User[]; // Users to display as selected (since they might not be in the current search results)
    onChange: (ids: number | number[], selectedUsers: User[]) => void;
    multiple?: boolean;
    label?: string;
    placeholder?: string;
    excludeIds?: number[];
    disabled?: boolean;
}

const UserSelector: React.FC<UserSelectorProps> = ({
    fetchUsers,
    selectedIds,
    selectedUsers = [],
    onChange,
    multiple = false,
    label,
    placeholder = 'Select user...',
    excludeIds = [],
    disabled = false
}) => {
    const { theme } = useTheme();
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [userList, setUserList] = useState<User[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);

    const wrapperRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const searchTimeout = useRef<NodeJS.Timeout | null>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const loadUsers = useCallback(async (query: string, pageNum: number, append: boolean = false) => {
        setIsLoading(true);
        try {
            const newUsers = await fetchUsers(query, pageNum);

            // Filter out excluded users if any (though backend search might not exclude them, we do it here for safety)
            // Actually, for pagination consistency, it's better if backend handles exclusion, but we can just visually disable or hide them.
            // For now, we'll just filter them out from the display list if they are in excludeIds.
            // However, if we filter them out, we might end up with fewer items than expected. 
            // Let's just keep them in the list but disable selection if needed, or filter them.
            // Filtering is safer for "excludeIds" usually meaning "don't show".

            const filteredNewUsers = newUsers.filter(u => !excludeIds.includes(u.id));

            setUserList(prev => append ? [...prev, ...filteredNewUsers] : filteredNewUsers);
            setHasMore(newUsers.length > 0); // Simple check: if we got results, there might be more. 
            // A better check would be if newUsers.length === limit. But we assume limit is constant (20).
            if (newUsers.length < 20) setHasMore(false);

        } catch (error) {
            console.error("Failed to load users", error);
        } finally {
            setIsLoading(false);
        }
    }, [fetchUsers, excludeIds]);

    // Initial load and search debounce
    useEffect(() => {
        if (!isOpen) return;

        if (searchTimeout.current) clearTimeout(searchTimeout.current);

        searchTimeout.current = setTimeout(() => {
            setPage(1);
            loadUsers(searchTerm, 1, false);
        }, 300);

        return () => {
            if (searchTimeout.current) clearTimeout(searchTimeout.current);
        };
    }, [searchTerm, isOpen, loadUsers]);

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
        if (scrollHeight - scrollTop <= clientHeight + 50 && !isLoading && hasMore) {
            const nextPage = page + 1;
            setPage(nextPage);
            loadUsers(searchTerm, nextPage, true);
        }
    };

    const handleSelect = (user: User) => {
        if (multiple) {
            const currentIds = Array.isArray(selectedIds) ? selectedIds : [];
            const currentSelectedUsers = [...selectedUsers];

            if (currentIds.includes(user.id)) {
                const newIds = currentIds.filter(id => id !== user.id);
                const newSelectedUsers = currentSelectedUsers.filter(u => u.id !== user.id);
                onChange(newIds, newSelectedUsers);
            } else {
                onChange([...currentIds, user.id], [...currentSelectedUsers, user]);
            }
        } else {
            onChange(user.id, [user]);
            setIsOpen(false);
        }
        // Don't clear search term on multi-select to allow selecting multiple from same search
        if (!multiple) setSearchTerm('');
    };

    const handleRemove = (idToRemove: number, e: React.MouseEvent) => {
        e.stopPropagation();
        if (multiple && Array.isArray(selectedIds)) {
            const newIds = selectedIds.filter(id => id !== idToRemove);
            const newSelectedUsers = selectedUsers.filter(u => u.id !== idToRemove);
            onChange(newIds, newSelectedUsers);
        } else {
            onChange(multiple ? [] : 0, []);
        }
    };

    // We use the passed selectedUsers for display. 
    // If selectedUsers is empty but selectedIds is not (initial load without full user objects), 
    // we might display "Loading..." or just the ID (bad UX).
    // The parent component is responsible for providing the initial selected user objects.

    // Fallback for display if selectedUsers is missing but we have IDs? 
    // We can try to find them in userList, but userList might not have them yet.
    // So we strictly rely on selectedUsers prop for "chips" or "selected value" display.

    // Merge selectedUsers with any found in userList that match selectedIds to ensure we have latest data?
    // No, selectedUsers prop should be the source of truth for display.

    return (
        <div className="w-full" ref={wrapperRef}>
            {label && (
                <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    {label}
                </label>
            )}

            <div
                className={`
                    relative w-full border rounded-md min-h-[42px] px-3 py-2 cursor-pointer
                    ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}
                    ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
                    focus-within:ring-2 focus-within:ring-indigo-500
                `}
                onClick={() => !disabled && setIsOpen(!isOpen)}
            >
                <div className="flex flex-wrap gap-2 items-center pr-6">
                    {selectedUsers.length > 0 ? (
                        multiple ? (
                            selectedUsers.map(user => (
                                <span
                                    key={user.id}
                                    className={`
                                        inline-flex items-center px-2 py-1 rounded text-sm
                                        ${theme === 'dark' ? 'bg-indigo-900 text-indigo-200' : 'bg-indigo-100 text-indigo-800'}
                                    `}
                                >
                                    {user.full_name}
                                    <button
                                        onClick={(e) => handleRemove(user.id, e)}
                                        className="ml-1 hover:text-indigo-500 focus:outline-none"
                                    >
                                        <X size={14} />
                                    </button>
                                </span>
                            ))
                        ) : (
                            <span className={`block truncate ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>
                                {selectedUsers[0].full_name} ({selectedUsers[0].email})
                            </span>
                        )
                    ) : (
                        <span className={`text-gray-400`}>{placeholder}</span>
                    )}
                </div>

                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                    <ChevronDown size={16} />
                </div>
            </div>

            {isOpen && !disabled && (
                <div className={`
                    absolute z-50 mt-1 w-full rounded-md shadow-lg max-h-60 overflow-hidden flex flex-col
                    ${theme === 'dark' ? 'bg-gray-800 border border-gray-600' : 'bg-white border border-gray-200'}
                `}
                    style={{ width: wrapperRef.current?.offsetWidth }}
                >
                    <div className={`p-2 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                        <div className="relative">
                            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                            <input
                                type="text"
                                className={`
                                    w-full pl-8 pr-3 py-1.5 text-sm rounded border focus:outline-none focus:ring-2 focus:ring-indigo-500
                                    ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-gray-50 border-gray-300 text-gray-900'}
                                `}
                                placeholder="Search users..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                autoFocus
                            />
                        </div>
                    </div>

                    <div
                        className="overflow-y-auto flex-1"
                        onScroll={handleScroll}
                        ref={dropdownRef}
                    >
                        {userList.length > 0 ? (
                            userList.map(user => {
                                const isSelected = Array.isArray(selectedIds)
                                    ? selectedIds.includes(user.id)
                                    : selectedIds === user.id;

                                return (
                                    <div
                                        key={user.id}
                                        className={`
                                            px-4 py-2 text-sm cursor-pointer flex items-center justify-between
                                            ${theme === 'dark' ? 'hover:bg-gray-700 text-gray-200' : 'hover:bg-gray-50 text-gray-900'}
                                            ${isSelected ? (theme === 'dark' ? 'bg-indigo-900/30' : 'bg-indigo-50') : ''}
                                        `}
                                        onClick={() => handleSelect(user)}
                                    >
                                        <div className="flex flex-col">
                                            <span className="font-medium">{user.full_name}</span>
                                            <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{user.email}</span>
                                        </div>
                                        {isSelected && <Check size={16} className="text-indigo-600" />}
                                    </div>
                                );
                            })
                        ) : (
                            !isLoading && (
                                <div className={`px-4 py-3 text-sm text-center ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
                                    No users found
                                </div>
                            )
                        )}

                        {isLoading && (
                            <div className={`px-4 py-2 text-center ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                <Loader size={16} className="animate-spin inline-block mr-2" />
                                Loading...
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserSelector;
